import React, { useCallback, useEffect, useState } from "react";
import dynamic from "next/dynamic";

import { useRouter } from "next/router";
import { Formik, Field, Form } from "formik";
import { object, string, number } from "yup";
import { FormattedNumberInput, Input } from "../components/Input";
import Button from "../components/Button";
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Checkbox,
  FormHelperText,
  Text,
  HStack,
  Wrap,
  WrapItem,
  Flex,
  Button as ChakraButton,
  VStack,
  Box,
  Icon,
  Progress,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Show,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import Loader from "../components/Loader";
import useDonation from "../hooks/useDonations";

import {
  formatMoney,
  getMaxTip,
  getMinTip,
  getTiktokVideoId,
  shortHandNumber,
  VENDOR_CUT,
  getInstagramVideoId,
  getMinTipIDR,
  getMaxTipIDR,
  getMinTipPHP,
  getMaxTipPHP,
  getMinTipJPY,
  getMaxTipJPY,
} from "../utils/";
import { Large, P } from "../components/Type";
import Link from "../components/Link";
import { VoteSelector } from "./StreamerPage/VoteSelector";
import { MediaSelector } from "./StreamerPage/MediaSelector";
import getVideoId from "get-video-id";
import { Username, UsernameBadge } from "../components/Username";
import isEmail from "validator/lib/isEmail";
import { Trans, msg, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { PaymentMethods } from "./PaymentMethods";
import { getFormError } from "../i18n";
import {
  PaymentMethodSelectorProvider,
  usePaymentMethodSelector,
} from "../contexts/paymentMethodSelector";
import { HiFire } from "react-icons/hi";
import { FaInfoCircle, FaShoppingCart, FaTimesCircle } from "react-icons/fa";
import { BsBoxArrowUpRight, BsInfoCircle } from "react-icons/bs";
import { FaCheckCircle } from "react-icons/fa";
import { FaClock, FaShareNodes } from "react-icons/fa6";
import { useWishlistDonatorPublic } from "../hooks/useWishlistDonatorPublic";
import PurchaseProof from "./Wishlist/Detail/PurchaseProof";
import { useFonts } from "../components/FontSelector/useFont";
import ShareModal from "./Wishlist/Share";

const GiphyContextProvider = dynamic(
  () => {
    return import("../contexts/giphy").then((mod) => {
      return mod.GiphyContextProvider;
    });
  },
  { ssr: false }
);

const baseYupModel = {
  from: string()
    .required(msg`Nama tidak boleh kosong`)
    .max(32, msg`Nama terlalu panjang`)
    .test(
      "not-readable-domain",
      (message) => msg`Nama tidak diperbolehkan`,
      async (value = "") => {
        return await !isReadableAsDomain(value);
      }
    ),

  message: string()
    .required(msg`Pesan tidak boleh kosong`)
    .max(140),
  ytStart: number().moreThan(-1, msg`Tidak boleh negatif`),
  email: string()
    .required(msg`Masukkan email kamu`)
    .email(msg`Masukan email kamu`)
    .test(
      "is-valid",
      (message) => msg`Periksa lagi email kamu`,
      async (value = "") => {
        return await isEmail(value);
      }
    ),
  phone: string(),
};

const getYupSchema = (currency, wishlist_min_donation) => {
  const MINIMUM_TIP_AMOUNT = wishlist_min_donation;
  const MAXIMUM_TIP_AMOUNT = getMaxTip("IDR");

  return object().shape({
    ...baseYupModel,
    amount: number()
      .required(msg`Masukkan nominal yang ingin diberikan`)
      .min(
        MINIMUM_TIP_AMOUNT,
        msg`Nominal minimum ${formatMoney(MINIMUM_TIP_AMOUNT, {
          currency,
        })}`
      )
      .max(
        MAXIMUM_TIP_AMOUNT,
        msg`Nominal maksimum ${formatMoney(MAXIMUM_TIP_AMOUNT, {
          currency,
        })}`
      ),
  });
};

function isReadableAsDomain(str) {
  // Remove all white spaces
  var noSpaces = str.replace(/\s/g, "");

  if (noSpaces.indexOf("http") >= 0 || noSpaces.indexOf(":/") >= 0) {
    return true;
  }

  // Use regex to check for domain name format
  var domainRegex = /^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}$/;

  return domainRegex.test(noSpaces.toLowerCase());
}

const phoneRegex = new RegExp("^08\\d{7,11}$");

const getGatewayTipRange = (currency, gateway) => {
  switch (currency) {
    case "PHP":
      return { min: getMinTipPHP(gateway), max: getMaxTipPHP(gateway) };
    case "JPY":
      return { min: getMinTipJPY(gateway), max: getMaxTipJPY(gateway) };
    case "IDR":
    default:
      return { min: getMinTipIDR(gateway), max: getMaxTipIDR(gateway) };
  }
};

const isAmountWithinGatewayRange = ({ currency, gateway, amount }) => {
  if (!gateway) return true; // belum pilih payment method
  const n = Number(amount);
  if (!Number.isFinite(n)) return false;

  const { min, max } = getGatewayTipRange(currency, gateway);
  return n >= min && n <= max;
};

const quickAmountColors = [
  "saweria-green.normal",
  "saweria-blue.normal",
  "saweria-yellow.normal",
  "saweria-purple.normal",
  "saweria-pink.normal",
];

const WishlistForm = ({
  wishlist,
  onBack,
  plus,
  overlay,
  vote,
  streamerId,
  isGopayEnabled,
  isOvoEnabled,
  isDanaEnabled,
  isLinkAjaEnabled,
  isQRISEnabled,
  isBCAVAEnabled,
  isMandiriVAEnabled,
  isBRIVAEnabled,
  isBNIVAEnabled,
  isPermataVAEnabled,
  isBJBVAEnabled,
  isBSIVAEnabled,
  isCIMBVAEnabled,
  currUsername = "",
  streamerUsername = "",
  currEmail = "",
  currency,
  transactionFeePolicy = null,
}) => {
  const titleFont = "Comfortaa";
  const router = useRouter();

  const [page, setPage] = useState(1);
  const [donators, setDonators] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const observer = React.useRef(null);

  const { isOpen: isOpenShare, onOpen: onOpenShare, onClose: onCloseShare } = useDisclosure();

  useFonts({ fonts: [{ family: titleFont }] });

  const { data, isLoading } = useWishlistDonatorPublic(
    wishlist?.product_slug,
    page // pastikan hook bisa terima page
  );

  useEffect(() => {
    if (data?.donators) {
      setDonators((prev) => [...prev, ...data.donators]);

      if (donators.length + data.donators.length >= data.total_donators) {
        setHasMore(false);
      }
    }
  }, [data]);

  const lastDonatorRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore]
  );

  const { createDonation, calculateAmountToPayWishlist } = useDonation();
  const yupSchema = getYupSchema(currency, wishlist.min_donation);
  const {
    min: minimumAlert,
    tts: tts,
    min_tts: minTTS,
    quick_amounts: quickAmounts,
  } = overlay;
  const { _ } = useLingui();
  if (!wishlist) {
    return <Loader></Loader>;
  }
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selected, setSelected] = useState(null);

  const handleClick = (proof) => {
    setSelected(proof);
    onOpen();
  };

  // all fee in wishlist paid by tipper
  transactionFeePolicy = "TIPPER";

  return (
    <>
      {wishlist?.status === "END" ? (
        <>
          <ChakraButton bg={"transparent"} mb={4} onClick={() => router.push(`/${streamerUsername}/wishlist`)} p={0}>
            ← <Trans>Kembali</Trans>
          </ChakraButton>

          <Box
            w="100%"
            borderRadius="6px"
            bg="#f2f7f5"
            border="0.7px solid #000"
            p="20px"
            display="flex"
            flexDirection="column"
            gap="16px"
            fontSize="18px"
            fontFamily="'IBM Plex Mono'"
            color="#000"
            textAlign="left"
            mb="24px"
          >
            {/* Top Section */}
            <Flex w="100%" gap="24px" align="flex-start" mb="16px">
              <Box
                flexShrink={0}
                mr="24px"
                borderRadius="6px"
                overflow="hidden"
                w="120px"
                h="120px"
                position="relative"
              >
                <Image
                  src={wishlist.image_url}
                  alt=""
                  width={120}
                  height={120}
                  fill
                  style={{ objectFit: "cover", borderRadius: "6px" }}
                />
              </Box>

              {/* Product Info */}
              <VStack
                align="flex-start"
                justify="center"
                gap="12px"
                flex="1"
                minW={0}
              >
                {/* Header */}
                <Box
                  display={{ base: "block", md: "flex" }}
                  align="flex-start"
                  gap="6.8px"
                  w="100%"
                  minW={0}
                >
                  <Text
                    textAlign="left"
                    fontWeight="bold"
                    lineHeight="28px"
                    fontSize={{ base: "md", md: "lg" }}
                    minW={0}
                    mb={{ base: 1, md: 0 }}
                  >
                    {wishlist.product_name}
                  </Text>

                  <Flex flex="1" />

                  <Link
                    href={wishlist.link}
                    textDecoration="none"
                    flexShrink={0}
                  >
                    <Flex
                      display="inline-flex"
                      bg="#eaefed"
                      borderRadius="6px"
                      align="center"
                      gap="8px"
                      px="8px"
                      py="4px"
                      fontSize="14px"
                      color="#3182ce"
                      minW={0}
                    >
                      <Text
                        fontWeight="600"
                        textDecoration="underline"
                        lineHeight="20px"
                        isTruncated
                      >
                        <Trans>Barang</Trans>
                      </Text>
                      <Icon as={BsBoxArrowUpRight} boxSize="14px" ml="8px" />
                    </Flex>
                  </Link>
                </Box>

                {/* Progress */}
                <Show above="md">
                  <VStack
                    align="flex-start"
                    gap="8px"
                    fontSize="14px"
                    color="#dd6b20"
                    w="100%"
                    minW={0}
                  >
                    {wishlist.type === "CROWDFUND" && (
                      <Box w="100%">
                        <Progress
                          value={
                            (wishlist.total_donation / wishlist.total_price) *
                            100
                          }
                          size="sm"
                          borderRadius="99px"
                          sx={{
                            "& > div": {
                              backgroundColor: "#3182CE",
                            },
                            backgroundColor: "#DDDEDE",
                          }}
                        />
                      </Box>
                    )}

                    <Flex align="center" gap="4px" minW={0}>
                      {wishlist.type === "FULLY_FUNDED" &&
                        wishlist.total_donation >= wishlist.total_price ? (
                        <Icon
                          as={FaCheckCircle}
                          boxSize="14px"
                          marginRight="4px"
                          color="#38A169"
                        />
                      ) : (
                        <Icon
                          as={HiFire}
                          boxSize="14px"
                          marginRight="4px"
                          color="#DD6B20"
                        />
                      )}
                      <Text
                        fontSize="14px"
                        fontWeight="600"
                        isTruncated
                        minW={0}
                      >
                        {wishlist.type === "FULLY_FUNDED" ? (
                          wishlist.total_donation >= wishlist.total_price ? (
                            <Trans>Sudah ada dukungan</Trans>
                          ) : (
                            <Trans>Beliin langsung yuk!</Trans>
                          )
                        ) : wishlist.total_donator > 0 ? (
                          <>
                            {wishlist.total_donator}{" "}
                            <Trans>orang ikut patungan</Trans>
                          </>
                        ) : (
                          <Trans>Ikut patungan yuk!</Trans>
                        )}
                      </Text>
                    </Flex>

                    <Flex align="center" gap="4px" color="#000" minW={0}>
                      <Icon
                        as={FaShoppingCart}
                        boxSize="14px"
                        color="#3182CE"
                        marginRight="4px"
                      />
                      <Text fontSize="14px" isTruncated minW={0}>
                        {wishlist.ecommerce_name}
                      </Text>
                    </Flex>
                  </VStack>

                  {/* Price */}
                  <Text fontSize="16px" lineHeight="24px" isTruncated minW={0}>
                    {wishlist.type === "FULLY_FUNDED" ? (
                      <b>
                        {formatMoney(wishlist.total_price, {
                          formatWithSymbol: true,
                          currency: currency ?? "IDR",
                        })}{" "}
                      </b>
                    ) : (
                      <>
                        <b>
                          {formatMoney(wishlist.total_donation, {
                            formatWithSymbol: true,
                            currency: currency ?? "IDR",
                          })}{" "}
                        </b>
                        /{" "}
                        {formatMoney(wishlist.total_price, {
                          formatWithSymbol: true,
                          currency: currency ?? "IDR",
                        })}
                      </>
                    )}
                  </Text>
                </Show>
              </VStack>
            </Flex>

            <Show below="md">
              {/* Progress */}
              <VStack
                align="flex-start"
                gap="8px"
                fontSize="14px"
                color="#dd6b20"
                w="100%"
                minW={0}
              >
                {wishlist.type === "CROWDFUND" && (
                  <Box w="100%">
                    <Progress
                      value={
                        (wishlist.total_donation / wishlist.total_price) * 100
                      }
                      size="sm"
                      borderRadius="99px"
                      sx={{
                        "& > div": {
                          backgroundColor: "#3182CE",
                        },
                        backgroundColor: "#DDDEDE",
                      }}
                    />
                  </Box>
                )}

                <Flex align="center" gap="4px" minW={0}>
                  {wishlist.type === "FULLY_FUNDED" &&
                    wishlist.total_donation >= wishlist.total_price ? (
                    <Icon
                      as={FaCheckCircle}
                      boxSize="14px"
                      marginRight="4px"
                      color="#38A169"
                    />
                  ) : (
                    <Icon
                      as={HiFire}
                      boxSize="14px"
                      marginRight="4px"
                      color="#DD6B20"
                    />
                  )}
                  <Text fontSize="14px" fontWeight="600" isTruncated minW={0}>
                    {wishlist.type === "FULLY_FUNDED" ? (
                      wishlist.total_donation >= wishlist.total_price ? (
                        <Trans>Sudah ada dukungan</Trans>
                      ) : (
                        <Trans>Beliin langsung yuk!</Trans>
                      )
                    ) : wishlist.total_donator > 0 ? (
                      <>
                        {wishlist.total_donator}{" "}
                        <Trans>orang ikut patungan</Trans>
                      </>
                    ) : (
                      <Trans>Ikut patungan yuk!</Trans>
                    )}
                  </Text>
                </Flex>

                <Flex align="center" gap="4px" color="#000" minW={0}>
                  <Icon
                    as={FaShoppingCart}
                    boxSize="14px"
                    color="#3182CE"
                    marginRight="4px"
                  />
                  <Text fontSize="14px" isTruncated minW={0}>
                    {wishlist.ecommerce_name}
                  </Text>
                </Flex>
              </VStack>

              {/* Price */}
              <Text
                fontSize="16px"
                lineHeight="24px"
                isTruncated
                minW={0}
                mb={3}
                mt={1}
              >
                {wishlist.type === "FULLY_FUNDED" ? (
                  <b>
                    {formatMoney(wishlist.total_price, {
                      formatWithSymbol: true,
                      currency: currency ?? "IDR",
                    })}{" "}
                  </b>
                ) : (
                  <>
                    <b>
                      {formatMoney(wishlist.total_donation, {
                        formatWithSymbol: true,
                        currency: currency ?? "IDR",
                      })}{" "}
                    </b>
                    /{" "}
                    {formatMoney(wishlist.total_price, {
                      formatWithSymbol: true,
                      currency: currency ?? "IDR",
                    })}
                  </>
                )}
              </Text>
            </Show>

            <Accordion allowToggle w="100%">
              <AccordionItem border="1px solid #dddede" borderRadius="6px">
                <h2>
                  <AccordionButton
                    borderTopRadius="6px"
                    bg="#eaefed!important"
                    p="16px"
                    fontSize="14px"
                    _expanded={{ bg: "#dfe5e3" }}
                  >
                    <Flex align="center" gap="8px" w="100%" minW={0}>
                      <Text isTruncated><Trans>Harga termasuk ongkir ya!</Trans></Text>
                    </Flex>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel bg="#eaefed">
                  <VStack
                    align="center"
                    borderRadius="6px"
                    bg="#f2f7f5"
                    p="16px"
                    gap="12px"
                    fontSize="14px"
                    w="100%"
                  >
                    <VStack align="flex-start" gap="8px" w="100%" minW={0}>
                      <Flex
                        align="flex-start"
                        justify="space-between"
                        w="100%"
                        minW={0}
                      >
                        <Text isTruncated><Trans>Harga Barang</Trans></Text>
                        <Text isTruncated>
                          {formatMoney(wishlist.price, {
                            formatWithSymbol: true,
                            currency: currency ?? "IDR",
                          })}
                        </Text>
                      </Flex>
                      <Flex
                        align="flex-start"
                        justify="space-between"
                        w="100%"
                        minW={0}
                      >
                        <Text isTruncated><Trans>Ongkir</Trans></Text>
                        <Text isTruncated>
                          {formatMoney(wishlist.delivery_fee, {
                            formatWithSymbol: true,
                            currency: currency ?? "IDR",
                          })}
                        </Text>
                      </Flex>
                    </VStack>

                    <Box w="100%" borderTop="1px dashed #d0d0d0" />

                    <Flex
                      align="flex-start"
                      justify="space-between"
                      w="100%"
                      minW={0}
                    >
                      <Text fontWeight="600" isTruncated>
                        <Trans>Total Harga</Trans>
                      </Text>
                      <Text fontWeight="600" isTruncated>
                        {formatMoney(wishlist.total_price, {
                          formatWithSymbol: true,
                          currency: currency ?? "IDR",
                        })}
                      </Text>
                    </Flex>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>
          {/* End of Wishlist Detail */}

          {wishlist?.status === "ACTIVE" &&
            wishlist?.type === "FULLY_FUNDED" &&
            wishlist?.transaction_status === "SUCCESS" && (
              <Box
                w="100%"
                borderRadius="6px"
                bg="#f2f7f5"
                border="0.7px solid #000"
                p="20px"
                display="flex"
                flexDirection="column"
                gap="16px"
                fontSize="18px"
                fontFamily="'IBM Plex Mono'"
                color="#000"
                textAlign="left"
                mb="24px"
              >
                <Text color="#000000" fontSize="xl" mb="4">
                  <Trans>Pendukung</Trans>
                </Text>
                <Flex justifyContent="space-between" fontSize="md">
                  <Username
                    w={"min-content"}
                    username={currUsername}
                  ></Username>
                  <Text fontWeight="500">
                    {formatMoney(wishlist.total_price, { currency })}
                  </Text>
                </Flex>
              </Box>
            )}

          <PurchaseProof
            purchaseProof={wishlist.purchase_proofs}
            fonts={{ titleFont }}
            highlighted={true}
          />

          <Box
            w="100%"
            borderRadius="6px"
            bg="#f2f7f5"
            border="0.7px solid #000"
            p="20px"
            mt="24px"
            mb="24px"
            height="400px"
            overflow="auto"
          >
            <Text color="#000000" fontSize="xl" mb="4">
              <Trans>Didukung {data?.total_donators ?? 0} Orang</Trans>
            </Text>

            {donators.map((d, idx) => {
              if (donators.length === idx + 1) {
                // elemen terakhir → kasih ref observer
                return (
                  <Flex
                    key={idx}
                    justifyContent="space-between"
                    fontSize="md"
                    py="4px"
                    ref={lastDonatorRef}
                  >
                    <Text display="flex" fontWeight="500">
                      {d.rank}.
                      <Username
                        w="100%"
                        username={d.donator_name}
                        ml="5px"
                        isTruncated
                        overflow="hidden"
                      />
                    </Text>
                    <Text fontWeight="500">
                      {formatMoney(d.total_donation, { currency: currency ?? "IDR" })}
                    </Text>
                  </Flex>
                );
              } else {
                return (
                  <Flex
                    key={idx}
                    justifyContent="space-between"
                    fontSize="md"
                    py="4px"
                  >
                    <Text display="flex" fontWeight="500">
                      {d.rank}.
                      <Username
                        w="100%"
                        username={d.donator_name}
                        ml="5px"
                        isTruncated
                        overflow="hidden"
                      />
                    </Text>
                    <Text fontWeight="500">
                      {formatMoney(d.total_donation, { currency: currency ?? "IDR" })}
                    </Text>
                  </Flex>
                );
              }
            })}

            {isLoading && <Text mt="4">Loading...</Text>}
          </Box>
        </>
      ) : (
        <>
          <Flex justifyContent="space-between" alignItems="center" mb={4} w="100%">
            <ChakraButton
              bg={"transparent"}
              onClick={() => router.push(`/${streamerUsername}/wishlist`)}
              p={0}
            >
              ← <Trans>Kembali</Trans>
            </ChakraButton>

            <ChakraButton
              bg={"transparent"}
              fontFamily="'IBM Plex Mono'"
              onClick={(e) => {
                e.stopPropagation();
                onOpenShare();
              }}
              p={0}
              leftIcon={<FaShareNodes />}
            >
              <Trans>Share</Trans>
            </ChakraButton>
          </Flex>

          <ShareModal
            isOpen={isOpenShare}
            onClose={onCloseShare}
            onOpen={onOpenShare}
            wishlist={wishlist}
            fonts={{ bodyFont: "IBM Plex Mono", titleFont: "Comfortaa", modalFont: "Inter" }}
            username={streamerUsername}
          />


          {wishlist?.status === "ACTIVE" &&
            wishlist?.type === "FULLY_FUNDED" &&
            wishlist?.transaction_status === "SUCCESS" && (
              <>
                <Flex
                  w="100%"
                  bg="#DCEEFF"
                  border="1px solid #3182CE"
                  p="16px"
                  gap="16px"
                  fontSize="md"
                  fontFamily="'IBM Plex Mono'"
                  color="#000"
                  textAlign="left"
                  mb="24px"
                  borderRadius="8px"
                  align="center"
                >
                  <FaInfoCircle size={22} color="#3182CE" />
                  <Text color="#000000" ml="2">
                    <Trans>Udah didukung nih, tinggal nunggu Barang dibeli 🙏</Trans>
                  </Text>
                </Flex>
              </>
            )}

          {wishlist?.status === "ACTIVE" &&
            wishlist?.type === "FULLY_FUNDED" &&
            wishlist?.transaction_status === "PENDING" && (
              <Flex
                w="100%"
                bg="warning.surface"
                border="1px solid"
                borderColor="warning.main"
                p="16px"
                gap="16px"
                fontSize="md"
                fontFamily="'IBM Plex Mono'"
                color="#000"
                textAlign="left"
                mb="24px"
                borderRadius="8px"
                align="center"
              >
                <FaClock size={22} color="#E53E3E" />
                <Text color="#000000" ml="2">
                  <Trans>Lagi nunggu dibayar pendukung lain nih, coba lagi nanti ya!</Trans>
                </Text>
              </Flex>
            )}

          {wishlist?.is_open === false && (
            <Flex
              w="100%"
              bg="danger.surface"
              border="1px solid"
              borderColor="danger.main"
              p="16px"
              gap="16px"
              fontSize="md"
              fontFamily="'IBM Plex Mono'"
              color="#000"
              textAlign="left"
              mb="24px"
              borderRadius="8px"
              align="center"
            >
              <FaTimesCircle size={22} color="#E53E3E" />
              <Text color="#000000" ml="2">
                <Trans>Wishlist lagi nggak bisa didukung, coba lain kali ya!</Trans>
              </Text>
            </Flex>
          )}

          <Box
            w="100%"
            borderRadius="6px"
            bg="#f2f7f5"
            border="0.7px solid #000"
            p="20px"
            display="flex"
            flexDirection="column"
            gap="16px"
            fontSize="18px"
            fontFamily="'IBM Plex Mono'"
            color="#000"
            textAlign="left"
            mb="24px"
          >
            {/* Top Section */}
            <Flex w="100%" gap="24px" align="flex-start" mb="16px">
              <Box
                flexShrink={0}
                mr="24px"
                borderRadius="6px"
                overflow="hidden"
                w="120px"
                h="120px"
                position="relative"
              >
                <Image
                  src={wishlist.image_url}
                  alt=""
                  width={120}
                  height={120}
                  fill
                  style={{ objectFit: "cover", borderRadius: "6px" }}
                />
              </Box>

              {/* Product Info */}
              <VStack
                align="flex-start"
                justify="center"
                gap="12px"
                flex="1"
                minW={0}
              >
                {/* Header */}
                <Box
                  display={{ base: "block", md: "flex" }}
                  align="flex-start"
                  gap="6.8px"
                  w="100%"
                  minW={0}
                >
                  <Text
                    textAlign="left"
                    fontWeight="bold"
                    lineHeight="28px"
                    fontSize={{ base: "md", md: "lg" }}
                    minW={0}
                    mb={{ base: 1, md: 0 }}
                  >
                    {wishlist.product_name}
                  </Text>

                  <Flex flex="1" />

                  <Link
                    href={wishlist.link}
                    textDecoration="none"
                    flexShrink={0}
                  >
                    <Flex
                      display="inline-flex"
                      bg="#eaefed"
                      borderRadius="6px"
                      align="center"
                      gap="8px"
                      px="8px"
                      py="4px"
                      fontSize="14px"
                      color="#3182ce"
                      minW={0}
                    >
                      <Text
                        fontWeight="600"
                        textDecoration="underline"
                        lineHeight="20px"
                        isTruncated
                      >
                        <Trans>Barang</Trans>
                      </Text>
                      <Icon as={BsBoxArrowUpRight} boxSize="14px" ml="8px" />
                    </Flex>
                  </Link>
                </Box>

                {/* Progress */}
                <Show above="md">
                  <VStack
                    align="flex-start"
                    gap="8px"
                    fontSize="14px"
                    color="#dd6b20"
                    w="100%"
                    minW={0}
                  >
                    {wishlist.type === "CROWDFUND" && (
                      <Box w="100%">
                        <Progress
                          value={
                            (wishlist.total_donation / wishlist.total_price) *
                            100
                          }
                          size="sm"
                          borderRadius="99px"
                          sx={{
                            "& > div": {
                              backgroundColor: "#3182CE",
                            },
                            backgroundColor: "#DDDEDE",
                          }}
                        />
                      </Box>
                    )}

                    <Flex align="center" gap="4px" minW={0}>
                      {wishlist.type === "FULLY_FUNDED" &&
                        wishlist.total_donation >= wishlist.total_price ? (
                        <Icon
                          as={FaCheckCircle}
                          boxSize="14px"
                          marginRight="4px"
                          color="#38A169"
                        />
                      ) : (
                        <Icon
                          as={HiFire}
                          boxSize="14px"
                          marginRight="4px"
                          color="#DD6B20"
                        />
                      )}
                      <Text
                        fontSize="14px"
                        fontWeight="600"
                        isTruncated
                        minW={0}
                      >
                        {wishlist.type === "FULLY_FUNDED" ? (
                          wishlist.total_donation >= wishlist.total_price ? (
                            <Trans>Sudah ada dukungan</Trans>
                          ) : (
                            <Trans>Beliin langsung yuk!</Trans>
                          )
                        ) : wishlist.total_donator > 0 ? (
                          <>
                            {wishlist.total_donator}{" "}
                            <Trans>orang ikut patungan</Trans>
                          </>
                        ) : (
                          <Trans>Ikut patungan yuk!</Trans>
                        )}
                      </Text>
                    </Flex>

                    <Flex align="center" gap="4px" color="#000" minW={0}>
                      <Icon
                        as={FaShoppingCart}
                        boxSize="14px"
                        color="#3182CE"
                        marginRight="4px"
                      />
                      <Text fontSize="14px" isTruncated minW={0}>
                        {wishlist.ecommerce_name}
                      </Text>
                    </Flex>
                  </VStack>

                  {/* Price */}
                  <Text fontSize="16px" lineHeight="24px" isTruncated minW={0}>
                    {wishlist.type === "FULLY_FUNDED" ? (
                      <b>
                        {formatMoney(wishlist.total_price, {
                          formatWithSymbol: true,
                          currency: currency ?? "IDR",
                        })}{" "}
                      </b>
                    ) : (
                      <>
                        <b>
                          {formatMoney(wishlist.total_donation, {
                            formatWithSymbol: true,
                            currency: currency ?? "IDR",
                          })}{" "}
                        </b>
                        /{" "}
                        {formatMoney(wishlist.total_price, {
                          formatWithSymbol: true,
                          currency: currency ?? "IDR",
                        })}
                      </>
                    )}
                  </Text>
                </Show>
              </VStack>
            </Flex>

            <Show below="md">
              {/* Progress */}
              <VStack
                align="flex-start"
                gap="8px"
                fontSize="14px"
                color="#dd6b20"
                w="100%"
                minW={0}
              >
                {wishlist.type === "CROWDFUND" && (
                  <Box w="100%">
                    <Progress
                      value={
                        (wishlist.total_donation / wishlist.total_price) * 100
                      }
                      size="sm"
                      borderRadius="99px"
                      sx={{
                        "& > div": {
                          backgroundColor: "#3182CE",
                        },
                        backgroundColor: "#DDDEDE",
                      }}
                    />
                  </Box>
                )}

                <Flex align="center" gap="4px" minW={0}>
                  {wishlist.type === "FULLY_FUNDED" &&
                    wishlist.total_donation >= wishlist.total_price ? (
                    <Icon
                      as={FaCheckCircle}
                      boxSize="14px"
                      marginRight="4px"
                      color="#38A169"
                    />
                  ) : (
                    <Icon
                      as={HiFire}
                      boxSize="14px"
                      marginRight="4px"
                      color="#DD6B20"
                    />
                  )}
                  <Text fontSize="14px" fontWeight="600" isTruncated minW={0}>
                    {wishlist.type === "FULLY_FUNDED" ? (
                      wishlist.total_donation >= wishlist.total_price ? (
                        <Trans>Sudah ada dukungan</Trans>
                      ) : (
                        <Trans>Beliin langsung yuk!</Trans>
                      )
                    ) : wishlist.total_donator > 0 ? (
                      <>
                        {wishlist.total_donator}{" "}
                        <Trans>orang ikut patungan</Trans>
                      </>
                    ) : (
                      <Trans>Ikut patungan yuk!</Trans>
                    )}
                  </Text>
                </Flex>

                <Flex align="center" gap="4px" color="#000" minW={0}>
                  <Icon
                    as={FaShoppingCart}
                    boxSize="14px"
                    color="#3182CE"
                    marginRight="4px"
                  />
                  <Text fontSize="14px" isTruncated minW={0}>
                    {wishlist.ecommerce_name}
                  </Text>
                </Flex>
              </VStack>

              {/* Price */}
              <Text
                fontSize="16px"
                lineHeight="24px"
                isTruncated
                minW={0}
                mb={3}
                mt={1}
              >
                {wishlist.type === "FULLY_FUNDED" ? (
                  <b>
                    {formatMoney(wishlist.total_price, {
                      formatWithSymbol: true,
                      currency: currency ?? "IDR",
                    })}{" "}
                  </b>
                ) : (
                  <>
                    <b>
                      {formatMoney(wishlist.total_donation, {
                        formatWithSymbol: true,
                        currency: currency ?? "IDR",
                      })}{" "}
                    </b>
                    /{" "}
                    {formatMoney(wishlist.total_price, {
                      formatWithSymbol: true,
                      currency: currency ?? "IDR",
                    })}
                  </>
                )}
              </Text>
            </Show>

            <Accordion allowToggle w="100%">
              <AccordionItem border="1px solid #dddede" borderRadius="6px">
                <h2>
                  <AccordionButton
                    borderTopRadius="6px"
                    bg="#eaefed!important"
                    p="16px"
                    fontSize="14px"
                    _expanded={{ bg: "#dfe5e3" }}
                  >
                    <Flex align="center" gap="8px" w="100%" minW={0}>
                      <Text isTruncated><Trans>Harga termasuk ongkir ya!</Trans></Text>
                    </Flex>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel bg="#eaefed">
                  <VStack
                    align="center"
                    borderRadius="6px"
                    bg="#f2f7f5"
                    p="16px"
                    gap="12px"
                    fontSize="14px"
                    w="100%"
                  >
                    <VStack align="flex-start" gap="8px" w="100%" minW={0}>
                      <Flex
                        align="flex-start"
                        justify="space-between"
                        w="100%"
                        minW={0}
                      >
                        <Text isTruncated><Trans>Harga Barang</Trans></Text>
                        <Text isTruncated>
                          {formatMoney(wishlist.price, {
                            formatWithSymbol: true,
                            currency: currency ?? "IDR",
                          })}
                        </Text>
                      </Flex>
                      <Flex
                        align="flex-start"
                        justify="space-between"
                        w="100%"
                        minW={0}
                      >
                        <Text isTruncated><Trans>Ongkir</Trans></Text>
                        <Text isTruncated>
                          {formatMoney(wishlist.delivery_fee, {
                            formatWithSymbol: true,
                            currency: currency ?? "IDR",
                          })}
                        </Text>
                      </Flex>
                    </VStack>

                    <Box w="100%" borderTop="1px dashed #d0d0d0" />

                    <Flex
                      align="flex-start"
                      justify="space-between"
                      w="100%"
                      minW={0}
                    >
                      <Text fontWeight="600" isTruncated>
                        <Trans>Total Harga</Trans>
                      </Text>
                      <Text fontWeight="600" isTruncated>
                        {formatMoney(wishlist.total_price, {
                          formatWithSymbol: true,
                          currency: currency ?? "IDR",
                        })}
                      </Text>
                    </Flex>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>
          {/* End of Wishlist Detail */}

          {wishlist?.status === "ACTIVE" &&
            wishlist?.type === "FULLY_FUNDED" &&
            wishlist?.transaction_status === "SUCCESS" && (
              <Box
                w="100%"
                borderRadius="6px"
                bg="#f2f7f5"
                border="0.7px solid #000"
                p="20px"
                display="flex"
                flexDirection="column"
                gap="16px"
                fontSize="18px"
                fontFamily="'IBM Plex Mono'"
                color="#000"
                textAlign="left"
                mb="24px"
              >
                <Text color="#000000" fontSize="xl" mb="4">
                  <Trans>Pendukung</Trans>
                </Text>
                {donators?.map((d, idx) => (
                  <Flex
                    key={idx}
                    justifyContent="space-between"
                    fontSize="md"
                    py="4px"
                  >
                    <Text display="flex" fontWeight="500">
                      {d.rank}.
                      <Username
                        w="100%"
                        username={d.donator_name}
                        ml="5px"
                        isTruncated
                        overflow="hidden"
                      />
                    </Text>
                    <Text fontWeight="500">
                      {formatMoney(d.total_donation, { currency: currency ?? "IDR" })}
                    </Text>
                  </Flex>
                ))}
              </Box>
            )}

          {!["SUCCESS", "PENDING"].includes(wishlist?.transaction_status) &&
            wishlist?.is_open !== false && (
              <GiphyContextProvider>
                <PaymentMethodSelectorProvider>
                  <Formik
                    validationSchema={yupSchema}
                    initialValues={{
                      agree: false,
                      notUnderage: false,
                      from: currUsername,
                      message: "",
                      amount: "",
                      payment_type: "",
                      email: currEmail,
                      phone: "",
                      vote: "",
                      giphy: null,
                      yt: "",
                      ytStart: 0,
                      mediaType: null,
                      amountToPay: "",
                      pgFee: "",
                      platformFee: "",
                      currency,
                    }}
                    enableReinitialize
                    validate={async (val) => {
                      const error = {};

                      if (val.payment_type === "ovo") {
                        if (!phoneRegex.test(val.phone)) {
                          error["phone"] = msg`Format nomor telepon salah. Gunakan nomor lainnya`;
                        }
                        if (!val.phone) {
                          error[
                            "phone"
                          ] = msg`Masukkan nomor telepon untuk pembayaran ${val.payment_type.toUpperCase()}`;
                        }
                      }
                      if (val.payment_type === "qris") {
                        if (val.amountToPay > 10_000_000) {
                          error[
                            "payment_type"
                          ] = msg`Maksimal total pembayaran menggunakan QRIS adalah Rp10.000.000`;
                        }
                      }
                      if (!val.notUnderage) {
                        error[
                          "notUnderage"
                        ] = msg`Anda harus berusia 17 tahun atau lebih.`;
                      }
                      if (!val.agree) {
                        error[
                          "agree"
                        ] = msg`Anda harus menyetujui syarat dan ketentuan saweria.co`;
                      }
                      if (val.mediaType === "yt") {
                        const { yt } = val;
                        const { id, service } = getVideoId(yt);
                        if (service !== "youtube" || !id) {
                          error["yt"] = msg`Cek ulang URL youtube diatas`;
                        }
                      }
                      if (val.mediaType === "tiktok") {
                        const { tiktok } = val;
                        const { id, service } = await getTiktokVideoId(tiktok);
                        if (service !== "tiktok" || !id) {
                          error["tiktok"] = msg`Cek ulang URL tiktok diatas`;
                        }
                      }
                      if (val.mediaType === "reels") {
                        const { reels } = val;
                        const id = getInstagramVideoId(reels);
                        if (!id) {
                          error["reels"] = msg`Cek ulang URL reels diatas`;
                        }
                      }

                      return error;
                    }}
                    onSubmit={async (values, { setSubmitting }) => {
                      try {
                        const { amountToPay, pgFee, platformFee, ...valueRes } =
                          values;
                        await createDonation(streamerId, valueRes, wishlist.id);
                      } catch (error) {
                        console.log(error);
                      }
                      setSubmitting(false);
                    }}
                  >
                    {({
                      setFieldValue,
                      submitForm,
                      isSubmitting,
                      values,
                      setFieldError,
                      errors,
                    }) => {
                      const router = useRouter();
                      const {
                        selectedPaymentMethod,
                        setSelectedPaymentMethod,
                        setTransactionFeePolicy,
                        setTransactionAmount,
                        setPgFee,
                      } = usePaymentMethodSelector();
                      setTransactionFeePolicy(transactionFeePolicy);
                      setTransactionAmount(values.amount);
                      setPgFee(values.pgFee);

                      const onClickPaymentMethod = (type) => async () => {
                        const { min, max } = getGatewayTipRange(currency, type);
                        const amountValue = Number(values.amount);

                        if (!Number.isFinite(amountValue) || amountValue < min || amountValue > max) {
                          setFieldError(
                            "payment_type",
                            msg`Nominal harus di antara ${formatMoney(min, { currency })} - ${formatMoney(max, {
                              currency,
                            })} untuk metode pembayaran ini`
                          );
                          return;
                        }

                        setFieldError("payment_type", undefined);
                        setSelectedPaymentMethod(type);
                        await setFieldValue("payment_type", type, false);

                        const { username } = router.query;
                        const amountToPayRes = await calculateAmountToPayWishlist(username, {
                          ...values,
                          payment_type: type,
                        });

                        await setFieldValue("amountToPay", amountToPayRes.amount_to_pay);
                        await setFieldValue("pgFee", amountToPayRes.pg_fee);
                        await setFieldValue("platformFee", amountToPayRes.platform_fee);
                      };

                      const chooseVote = (vote) => {
                        setFieldValue("vote", vote);
                      };

                      if (isSubmitting) {
                        return <Loader />;
                      }

                      const amountNumber = Number(values.amount);
                      const isAmountFilled = values.amount !== "" && Number.isFinite(amountNumber);

                      const isCoreFormFilled =
                        isAmountFilled &&
                        Boolean(values.from?.trim()) &&
                        Boolean(values.email?.trim()) &&
                        Boolean(values.notUnderage) &&
                        Boolean(values.agree);

                      const shouldFilterByGatewayRange = isCoreFormFilled;

                      const isGatewayOutOfRange = (gateway) =>
                        shouldFilterByGatewayRange &&
                        !isAmountWithinGatewayRange({
                          currency,
                          gateway,
                          amount: amountNumber,
                        });

                      const disabledPayments = {
                        qris: isGatewayOutOfRange("qris"),
                        gopay: isGatewayOutOfRange("gopay"),
                        ovo: isGatewayOutOfRange("ovo"),
                        dana: isGatewayOutOfRange("dana"),
                        linkaja: isGatewayOutOfRange("linkaja"),
                        bca: isGatewayOutOfRange("bca"),
                        mandiri: isGatewayOutOfRange("mandiri"),
                        bri: isGatewayOutOfRange("bri"),
                        bni: isGatewayOutOfRange("bni"),
                        permata: isGatewayOutOfRange("permata"),
                        bjb: isGatewayOutOfRange("bjb"),
                        bsi: isGatewayOutOfRange("bsi"),
                        cimb: isGatewayOutOfRange("cimb"),
                        shopeepay: isGatewayOutOfRange("shopeepay"),
                        grabpay: isGatewayOutOfRange("grabpay"),
                        gcash: isGatewayOutOfRange("gcash"),
                        paymaya: isGatewayOutOfRange("paymaya"),
                        paypay: isGatewayOutOfRange("paypay"),
                        alipay: isGatewayOutOfRange("alipay"),
                        linepay: isGatewayOutOfRange("linepay"),
                        rakutenpay: isGatewayOutOfRange("rakutenpay"),
                        merpay: isGatewayOutOfRange("merpay"),
                        inter_cc: isGatewayOutOfRange("inter_cc"),
                        wechat_pay: isGatewayOutOfRange("wechat_pay"),
                      };

                      const selectedGateway = values.payment_type || selectedPaymentMethod;
                      const isSelectedPaymentAmountValid = selectedGateway
                        ? !disabledPayments[selectedGateway]
                        : true;

                      const allowPayment =
                        !Object.keys(errors).length &&
                        values.agree &&
                        isSelectedPaymentAmountValid;

                      return (
                        <Form>
                          <Field type="number" name="amount">
                            {({ field, form }) => {
                              const isFullyFunded =
                                wishlist.type === "FULLY_FUNDED";

                              // when fully funded, force amount = total_price
                              if (
                                isFullyFunded &&
                                form.values.amount !== wishlist.total_price
                              ) {
                                form.setFieldValue(
                                  "amount",
                                  wishlist.total_price
                                );
                              }
                              return (
                                <FormControl
                                  isRequired
                                  mb={3}
                                  isInvalid={
                                    form.errors.amount && form.touched.amount
                                  }
                                >
                                  <FormLabel htmlFor="amount">
                                    <Trans>Nominal:</Trans>
                                  </FormLabel>
                                  {isFullyFunded ? (
                                    <Text
                                      fontWeight="600"
                                      fontSize="16px"
                                      mt="20px"
                                      mb="20px"
                                    >
                                      {formatMoney(wishlist.total_price, {
                                        currency,
                                      })}
                                    </Text>
                                  ) : (
                                    <>
                                      <FormattedNumberInput
                                        currency={currency}
                                        mb={2}
                                        data-testid="amount-input"
                                        id="amount"
                                        placeholder={_(
                                          msg`Ketik jumlah dukungan`
                                        )}
                                        {...field}
                                        onChange={() => {
                                          setSelectedPaymentMethod();
                                          setFieldValue("payment_type", "");
                                          setFieldValue("amountToPay", "");
                                          setFieldValue("pgFee", "");
                                          setFieldValue("platformFee", "");
                                        }}
                                        form={form}
                                      />
                                      <FormErrorMessage mb={2}>
                                        {getFormError(form.errors.amount, _)}
                                      </FormErrorMessage>
                                      <HStack>
                                        <FormHelperText mb={2}>
                                          <Trans>Minimum alert:</Trans>
                                          <Text as="span" color="red.400">
                                            {` ${formatMoney(minimumAlert, {
                                              currency,
                                            })}`}
                                          </Text>
                                        </FormHelperText>
                                        {/* Show minimum tts if tts is enabled */}
                                        {tts ? (
                                          <FormHelperText mb={2}>
                                            <Trans>TTS:</Trans>
                                            <Text as="span" color="red.400">
                                              {` ${formatMoney(minTTS, {
                                                currency,
                                              })}`}
                                            </Text>
                                          </FormHelperText>
                                        ) : null}
                                      </HStack>

                                      <QuickAmounts
                                        quickAmounts={quickAmounts}
                                        currAmount={values.amount}
                                        setAmount={(amount) => {
                                          setFieldValue("amount", amount);
                                          setFieldValue("amountToPay", "");
                                          setFieldValue("pgFee", "");
                                          setFieldValue("platformFee", "");
                                          setSelectedPaymentMethod();
                                          setFieldValue("payment_type", "");
                                        }}
                                        currency={currency}
                                      />
                                    </>
                                  )}
                                </FormControl>
                              );
                            }}
                          </Field>
                          <Field type="text" name="from">
                            {({ field, form }) => {
                              return (
                                <FormControl
                                  isRequired
                                  isReadOnly={currUsername}
                                  mb={3}
                                  isInvalid={
                                    form.errors.from && form.touched.from
                                  }
                                >
                                  <FormLabel htmlFor="from">
                                    <Trans>Dari:</Trans>
                                  </FormLabel>
                                  {currUsername ? (
                                    <UsernameBadge
                                      w={"min-content"}
                                      username={currUsername}
                                    ></UsernameBadge>
                                  ) : (
                                    <Input
                                      {...field}
                                      data-testid="from-input"
                                      id="from"
                                      placeholder={_(msg`Budi`)}
                                    />
                                  )}
                                  <FormErrorMessage>
                                    {getFormError(form.errors.from, _)}
                                  </FormErrorMessage>
                                </FormControl>
                              );
                            }}
                          </Field>

                          <Field name="email">
                            {({ field, form }) => {
                              return (
                                <FormControl
                                  display={currEmail ? "none" : "block"}
                                  isRequired
                                  isReadOnly={currEmail}
                                  mb={3}
                                  isInvalid={
                                    form.errors.email && form.touched.email
                                  }
                                >
                                  <FormLabel htmlFor="email">
                                    <Trans>Email:</Trans>
                                  </FormLabel>
                                  <Input
                                    {...field}
                                    data-testid="email-input"
                                    type="email"
                                    id="email"
                                    placeholder={_(msg`budi@saweria.co`)}
                                  />
                                  <FormErrorMessage>
                                    {getFormError(form.errors.email, _)}
                                  </FormErrorMessage>
                                </FormControl>
                              );
                            }}
                          </Field>

                          <Field type="text" name="message">
                            {({ field, form }) => {
                              return (
                                <FormControl
                                  isRequired
                                  mb={3}
                                  isInvalid={
                                    form.errors.message && form.touched.message
                                  }
                                >
                                  <FormLabel htmlFor="message">
                                    <Trans>Pesan:</Trans>
                                  </FormLabel>
                                  <Input
                                    {...field}
                                    data-testid="message-input"
                                    id="message"
                                    placeholder={_(msg`Selamat pagi`)}
                                  />
                                  <FormErrorMessage>
                                    {getFormError(form.errors.message, _)}
                                  </FormErrorMessage>
                                </FormControl>
                              );
                            }}
                          </Field>

                          {["ovo"].includes(values.payment_type) ? (
                            <>
                              <Field name="phone">
                                {({ field, form }) => {
                                  return (
                                    <FormControl
                                      isRequired
                                      mb={3}
                                      isInvalid={
                                        form.errors.phone && form.touched.phone
                                      }
                                    >
                                      <FormLabel htmlFor="phone">
                                        <Trans>Nomor telepon:</Trans>
                                      </FormLabel>
                                      <Input
                                        {...field}
                                        type="tel"
                                        data-testid="phone-input"
                                        id="phone"
                                        placeholder={_(
                                          msg`Nomor yang terdaftar di e-wallet pilihan`
                                        )}
                                      />
                                      <FormErrorMessage>
                                        {getFormError(form.errors.phone, _)}
                                      </FormErrorMessage>
                                    </FormControl>
                                  );
                                }}
                              </Field>
                            </>
                          ) : null}
                          <Field name="notUnderage">
                            {({ field, form }) => {
                              return (
                                <FormControl
                                  isRequired
                                  mb="3"
                                  isInvalid={
                                    form.errors.notUnderage &&
                                    form.touched.notUnderage
                                  }
                                >
                                  <Checkbox
                                    data-testid="underage-checkbox"
                                    size="lg"
                                    {...field}
                                    isInvalid={
                                      form.errors.notUnderage &&
                                      form.touched.notUnderage
                                    }
                                    borderColor="black"
                                    isChecked={form.values.notUnderage}
                                  >
                                    <P fontSize="0.75em" mb={0}>
                                      <Trans>
                                        Saya berusia 17 tahun atau lebih
                                      </Trans>
                                    </P>
                                  </Checkbox>

                                  <FormErrorMessage>
                                    {getFormError(form.errors.notUnderage, _)}
                                  </FormErrorMessage>
                                </FormControl>
                              );
                            }}
                          </Field>

                          <Field name="agree">
                            {({ field, form }) => {
                              return (
                                <FormControl
                                  isRequired
                                  mb="3"
                                  isInvalid={
                                    form.errors.agree && form.touched.agree
                                  }
                                >
                                  <Checkbox
                                    data-testid="agree-checkbox"
                                    size="lg"
                                    {...field}
                                    isInvalid={
                                      form.errors.agree && form.touched.agree
                                    }
                                    borderColor="black"
                                    isChecked={form.values.agree}
                                  >
                                    <P fontSize="0.75em" mb={0}>
                                      <Trans>
                                        Saya memahami dan menyetujui bahwa
                                        dukungan ini saya berikan secara
                                        sukarela untuk mendukung kreator, tidak
                                        dapat dikembalikan, bukan transaksi
                                        komersial, tidak digunakan untuk
                                        aktivitas ilegal, serta telah sesuai
                                        dengan{" "}
                                        <Link
                                          textDecoration="underline"
                                          href="/terms"
                                        >
                                          {`syarat dan ketentuan`}
                                        </Link>{" "}
                                        &{" "}
                                        <Link
                                          textDecoration="underline"
                                          href="/privacy"
                                        >
                                          kebijakan privasi
                                        </Link>{" "}
                                        saweria.co
                                      </Trans>
                                    </P>
                                  </Checkbox>

                                  <FormErrorMessage>
                                    {getFormError(form.errors.agree, _)}
                                  </FormErrorMessage>
                                </FormControl>
                              );
                            }}
                          </Field>
                          <Large fontSize={["lg", "xl"]}>
                            <Trans>Moda pembayaran:</Trans>
                          </Large>
                          <PaymentMethods
                            currency={currency}
                            isSubmitting={isSubmitting}
                            allowPayment={allowPayment}
                            enabledPayments={{
                              isDanaEnabled,
                              isQRISEnabled,
                              isGopayEnabled,
                              isLinkAjaEnabled,
                              isOvoEnabled,
                              isBCAVAEnabled,
                              isMandiriVAEnabled,
                              isBRIVAEnabled,
                              isBNIVAEnabled,
                              isPermataVAEnabled,
                              isBJBVAEnabled,
                              isBSIVAEnabled,
                              isCIMBVAEnabled,
                            }}
                            disabledPayments={disabledPayments}
                            donate={onClickPaymentMethod}
                          />
                          <Flex justifyContent="space-between" marginTop={6}>
                            <Flex flexDirection="column">
                              {transactionFeePolicy === "TIPPER" && (
                                <>
                                  <Large fontSize={["md"]}>
                                    <Trans>Jumlah Dukungan: </Trans>{" "}
                                    {formatMoney(values.amount, { currency })}
                                  </Large>
                                  <Large fontSize={["sm"]}>
                                    <Trans>Biaya Layanan:</Trans>{" "}
                                    {formatMoney(
                                      values.platformFee
                                        ? values.platformFee
                                        : 0,
                                      { currency }
                                    )}
                                  </Large>
                                  <Large fontSize={["sm"]}>
                                    <Trans>
                                      Biaya Pembayaran (Payment Gateway):
                                    </Trans>{" "}
                                    {formatMoney(
                                      values.pgFee ? values.pgFee : 0,
                                      {
                                        currency,
                                      }
                                    )}
                                  </Large>
                                </>
                              )}
                              <Large>
                                <Trans>Total:</Trans>{" "}
                                {formatMoney(values.amountToPay, {
                                  currency,
                                })}
                              </Large>
                              <Text color="red.500" fontSize="md">
                                {getFormError(errors.payment_type, _)}
                              </Text>
                            </Flex>
                            <Flex>
                              <Button
                                marginBottom={0}
                                disabled={!allowPayment || !selectedPaymentMethod}
                                data-testid="donate-button"
                                backgroundColor={`saweria-yellow.${
                                  allowPayment && selectedPaymentMethod ? "normal" : "dark"
                                }`}
                                type="button"
                                label={t`Kirim Dukungan`}
                                onClick={submitForm}
                              />
                            </Flex>
                          </Flex>
                        </Form>
                      );
                    }}
                  </Formik>
                </PaymentMethodSelectorProvider>
              </GiphyContextProvider>
            )}
        </>
      )}
    </>
  );
};

const QuickAmounts = ({ quickAmounts, currAmount, setAmount, currency }) => {
  const amountButtonHelper = quickAmounts
    .sort((a, b) => a - b)
    .map((amount, index) => {
      const label =
        currency === "JPY"
          ? `${formatMoney(amount, { currency })}`
          : shortHandNumber(amount);
      return (
        <WrapItem key={`button-helper-${amount}-${index}`} flexGrow={1}>
          <Button
            {...(currAmount == amount ? { boxShadow: "pressed" } : null)}
            transition={"box-shadow .1s , transform .2s "}
            type="button"
            disabled={currAmount == amount}
            onClick={(e) => {
              e.preventDefault();
              setAmount(amount);
            }}
            w="100%"
            m={0}
            label={label}
            bgColor={quickAmountColors[index % 4]}
          ></Button>
        </WrapItem>
      );
    });
  return <Wrap>{amountButtonHelper}</Wrap>;
};

export { WishlistForm, QuickAmounts };
